import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../context/PermissionsContext';
import { permissionForPath, getDefaultAppPath } from '../config/permissions';

function AccessDenied({ permission }) {
  return (
    <div className="access-denied-page">
      <h1>Access denied</h1>
      <p>You do not have permission to view this page{permission ? ` (${permission})` : ''}.</p>
      <p>Contact an administrator if you need access.</p>
    </div>
  );
}

/**
 * Guards routes by required permission key(s). Falls back to path-based permission when `permission` is omitted.
 */
const RequirePermission = ({
  children,
  permission = null,
  anyOf = null,
  redirectTo = '/',
}) => {
  const location = useLocation();
  const { hasPermission, hasAnyPermission, loading } = usePermissions();

  if (loading) {
    return (
      <div className="page-loading" role="status" aria-live="polite">
        Loading…
      </div>
    );
  }

  const required = permission || (anyOf ? null : permissionForPath(location.pathname));
  // Fail closed: every app route is mapped in ROUTE_PERMISSIONS, so an
  // unresolved permission means the route is unknown, not unrestricted.
  const allowed = anyOf ? hasAnyPermission(anyOf) : Boolean(required) && hasPermission(required);

  if (!allowed) {
    const isHome = location.pathname === redirectTo || location.pathname === '/';
    if (isHome) {
      const fallback = getDefaultAppPath(hasPermission);
      if (fallback && fallback !== '/' && fallback !== location.pathname) {
        return <Navigate to={fallback} replace />;
      }
      return <AccessDenied permission={required} />;
    }
    return (
      <Navigate
        to={redirectTo}
        replace
        state={{ from: location.pathname, denied: true }}
      />
    );
  }

  return children;
};

export default RequirePermission;
